import { useQuery } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { getBuzzTrackingTrend, getBuzzTrackingAverage } from "../../../../api/brandPerformanceApi";
import { performancePages } from "../../../../constants";
import { isNullOrUndefined } from "../../../../utils/utilityFunctions";
import BrandPerformanceTableTile from "../BrandPerformanceTableTile";
import BrandPerformanceTrendTile from "../BrandPerformanceTrendTile";

interface Props extends RouteComponentProps {
  onHandleRadioClick: any;
  nissanCompetitor: string;
  activePage: performancePages;
  diff: number;
  granularity: string;
}

const BuzzTracking = withRouter((props: Props) => {
  const { onHandleRadioClick, nissanCompetitor, diff, history, granularity } = props;

  const params = useSelector((state: RootStateOrAny) => state.parameters);
  const model = params.model;
  const attribute = params.attribute ? decodeURIComponent(params.attribute) : "Buzz";

  const attributeUpper = attribute.toUpperCase();
  const trendTitle =
    attributeUpper === "ATTENTION" ? "Attention Trend" :
    attributeUpper === "AD AWARENESS" ? "Ad Awareness Trend" :
    "Buzz Tracking Trend";
  const tableTitle =
    attributeUpper === "ATTENTION" ? "Attention Average" :
    attributeUpper === "AD AWARENESS" ? "Ad Awareness Average" :
    "Buzz Tracking Average";
  const tableHeaderLabel = tableTitle;

  const {
    data: chartData,
    isFetching: loadingTrend,
    refetch: refetchTrend,
  } = useQuery(
    ["buzzTrackingTrend", granularity, history.location.search],
    () => getBuzzTrackingTrend({ ...params, nissan_competitor: nissanCompetitor }),
    { enabled: false }
  );

  const {
    data: tableData,
    isFetching: loadingTable,
    refetch: refetchAverage,
  } = useQuery(
    ["buzzTrackingAverage", granularity, history.location.search],
    () => getBuzzTrackingAverage({ ...params, nissan_competitor: nissanCompetitor }),
    { enabled: false }
  );

  useEffect(() => {
    refetchTrend();
    refetchAverage();
  }, [history.location.search]);

  const columns = [
    {
      id: "model",
      Header: `${model == "Brand" ? "Brand" : "Model"} `,
      accessor: "model",
      defaultCanSort: true,
      sortDescFirst: true,
      sortType: "alphanumericIgnoreCase",
      Cell: (props1: { value: string }) => props1.value,
    },
    {
      Header: tableHeaderLabel,
      accessor: "percentage_average_score",
      id: "percentage_average_score",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "basicCustom",
      Cell: (props1: { cell: { row: { original: any } } }) => {
        const value = props1.cell.row.original?.["percentage_average_score"];
        return !isNullOrUndefined(value) ? `${value}%` : "n/a";
      },
    },
    {
      Header: "YoY",
      accessor: "yoy",
      id: "yoy",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "basicCustom",
      Cell: (props1: { cell: { row: { original: any } } }) => {
        const value = props1.cell.row.original?.["yoy"];
        return !isNullOrUndefined(value) ? `${value}%` : "n/a";
      },
    },
  ];

  const TrendTooltipParagraph = () => (
    <>
      <p>
        <span style={{ fontWeight: "bold" }}>{trendTitle} </span>
        shows the trend of the average score given to a make/model in the selected period.
      </p>
      <br />
    </>
  );

  const TableTooltipParagraphs = () => (
    <>
      <p>
        <span style={{ fontWeight: "bold" }}>{tableTitle} </span>
        shows the average score given to a make/model in the selected period.
      </p>
      <br />
    </>
  );

  return (
    <div className="brand-market-main-content">
      <BrandPerformanceTrendTile
        title={trendTitle}
        chartId="buzzTrackingTrend"
        chartData={chartData}
        loadingTrend={loadingTrend}
        diff={diff}
        TrendTooltipParagraph={TrendTooltipParagraph}
        nissanCompetitor={nissanCompetitor}
        onHandleRadioClick={onHandleRadioClick}
        chartGranularity={granularity}
        isPercentage={true}
      />
      <BrandPerformanceTableTile
        title={tableTitle}
        tableId="buzz_tracking_table"
        tableData={tableData}
        columns={columns}
        loadingTable={loadingTable}
        initialSortCol={{ id: "percentage_average_score", desc: true }}
        TableTooltipParagraphs={TableTooltipParagraphs}
      />
    </div>
  );
});

export default BuzzTracking;
